import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — drop-in replacement for `<input type="password">` with a
 * built-in show/hide eye toggle.
 *
 * Passes through every native `<input>` prop (value, onChange, required,
 * minLength, autoFocus, placeholder, className, data-testid, id, name,
 * autoComplete, disabled, aria-*, etc.) so existing pages keep working.
 *
 * The toggle button:
 *   - is absolutely positioned inside a relative wrapper (no layout shift)
 *   - inherits its own data-testid derived from the input's data-testid
 *     (e.g. `login-password` → `login-password-toggle`) for E2E tests
 *   - never triggers a form submit (`type="button"`)
 *
 * Callers whose input already had `pr-10` in className need no change.
 * The component adds `pr-10` automatically if it isn't present, so
 * padding on the right side leaves room for the eye icon.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const cls = className.includes("pr-") ? className : `${className} pr-10`;
  const toggleTestId = testId ? `${testId}-toggle` : undefined;
  return (
    <div className="relative w-full">
      <input
        {...rest}
        ref={ref}
        type={visible ? "text" : "password"}
        className={cls}
        data-testid={testId}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        data-testid={toggleTestId}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
